/**
 * 新题型模型 ↔ 旧 form-generator drawingList / workconfig 互转
 * 保证提交仍走 content + title 原接口结构，答题/批改页可继续解析
 */
import {
  TF_OPTIONS,
  DEFAULT_OPTIONS,
  acceptToFormats,
  formatsToAccept,
  createDefaultSettings,
  syncFormIdSeed,
  nextFormId
} from './questionTypes'

const detectType = (item) => {
  const tag = item.__config__ && item.__config__.tag
  const label = (item.__config__ && item.__config__.label) || ''
  if (tag === 'tinymce' || item.__vModel__ === 'explain') return 'desc'
  if (tag === 'el-input') return 'qa'
  if (tag === 'el-upload') return 'attach'
  if (tag === 'el-checkbox-group') return 'multi'
  if (tag === 'el-radio-group') {
    const opts = (item.__slot__ && item.__slot__.options) || []
    const isTf =
      opts.length === 2 &&
      opts.some((o) => o.value === '正确' || o.label === '正确') &&
      opts.some((o) => o.value === '错误' || o.label === '错误')
    if (isTf || label === '判断题') return 'tf'
    return 'single'
  }
  return 'qa'
}

// el-input 只接受 String/Number；旧数据里 defaultValue/resolve 可能是数组
const toInputString = (val) => {
  if (val == null || val === '') return ''
  if (Array.isArray(val)) return val.filter((v) => v != null && v !== '').join(',')
  if (typeof val === 'object') return ''
  return String(val)
}

const toSingleAnswer = (val) => {
  if (Array.isArray(val)) return val.length ? toInputString(val[0]) : ''
  return toInputString(val)
}

const toMultiAnswer = (val) => {
  if (Array.isArray(val)) return val.filter((v) => v != null && v !== '')
  if (val == null || val === '') return []
  return [val]
}

const normalizeOptions = (list) =>
  (list || DEFAULT_OPTIONS).map((o) => ({
    label: toInputString(o && o.label),
    value: o && o.value != null ? o.value : ''
  }))

const fromDrawingItem = (item) => {
  const config = item.__config__ || {}
  const type = detectType(item)
  if (type === 'desc') return null

  const formId = config.formId || nextFormId()
  const power = Array.isArray(config.power) ? config.power : []
  const powerValue =
    power[0] && power[0].powerVal ? power[0].powerVal : []

  const q = {
    id: formId,
    type,
    vModel: item.__vModel__ || `field${formId}`,
    title: toInputString(config.label),
    titleUrl: config.titleUrl || '',
    score: config.scoreValue != null && config.scoreValue !== '' ? Number(config.scoreValue) || config.scoreValue : (item.scoreValue != null && item.scoreValue !== '' ? Number(item.scoreValue) || item.scoreValue : null),
    resolve: toInputString(config.resolve != null && config.resolve !== '' ? config.resolve : item.resolve),
    power,
    powerValue: Array.isArray(powerValue) ? powerValue : [],
    answer: type === 'multi' ? [] : '',
    options: [],
    templateMd5: config.wrok_template || '',
    templateName: config.wrok_template_name || '',
    formats: acceptToFormats(item.accept)
  }

  if (type === 'tf') {
    q.options = TF_OPTIONS.map((o) => ({ ...o }))
    q.answer = toSingleAnswer(config.defaultValue)
  } else if (type === 'single') {
    q.options = normalizeOptions(item.__slot__ && item.__slot__.options)
    q.answer = toSingleAnswer(config.defaultValue)
  } else if (type === 'multi') {
    q.options = normalizeOptions(item.__slot__ && item.__slot__.options)
    q.answer = toMultiAnswer(config.defaultValue)
  } else if (type === 'qa') {
    q.answer = toInputString(config.defaultValue)
  }

  return q
}

export const fromDrawingList = (list = []) => {
  syncFormIdSeed(list)
  let explain = ''
  const questions = []
  list.forEach((item) => {
    const type = detectType(item)
    if (type === 'desc') {
      explain = (item.__config__ && item.__config__.defaultValue) || ''
    } else {
      const q = fromDrawingItem(item)
      if (q) questions.push(q)
    }
  })
  return { explain, questions }
}

const buildExplainItem = (html) => ({
  __config__: {
    label: '作业说明',
    showLabel: true,
    changeTag: false,
    labelWidth: null,
    tag: 'tinymce',
    tagIcon: 'rich-text',
    defaultValue: html || null,
    span: 24,
    layout: 'colFormItem',
    required: true,
    regList: [],
    document: 'http://tinymce.ax-z.cn',
    formId: 100,
    renderKey: `100${Date.now()}`
  },
  placeholder: '请输入作业说明',
  height: 300,
  branding: false,
  __vModel__: 'explain'
})

const toDrawingItem = (q) => {
  const formId = q.id || nextFormId()
  const baseConfig = {
    label: q.title || '',
    titleUrl: q.titleUrl || '',
    labelWidth: null,
    showLabel: true,
    layout: 'colFormItem',
    span: 24,
    required: true,
    regList: [],
    changeTag: false,
    formId,
    renderKey: `${formId}${Date.now()}`,
    scoreValue: q.score === '' || q.score == null ? '' : q.score,
    resolve: q.resolve || '',
    power: q.power || []
  }

  if (q.type === 'qa') {
    return {
      __config__: {
        ...baseConfig,
        tag: 'el-input',
        tagIcon: 'textarea',
        defaultValue: q.answer || undefined,
        document: 'https://element.eleme.cn/#/zh-CN/component/input'
      },
      type: 'textarea',
      autosize: { minRows: 4, maxRows: 4 },
      style: { width: '100%' },
      maxlength: null,
      'show-word-limit': false,
      readonly: false,
      disabled: false,
      __vModel__: q.vModel || `field${formId}`
    }
  }

  if (q.type === 'tf') {
    return {
      __config__: {
        ...baseConfig,
        tag: 'el-radio-group',
        tagIcon: 'radio',
        defaultValue: q.answer || undefined,
        optionType: 'default',
        border: false,
        document: 'https://element.eleme.cn/#/zh-CN/component/radio'
      },
      __slot__: {
        options: TF_OPTIONS.map((o) => ({ ...o }))
      },
      style: {},
      size: 'medium',
      disabled: false,
      __vModel__: q.vModel || `field${formId}`
    }
  }

  if (q.type === 'single') {
    return {
      __config__: {
        ...baseConfig,
        tag: 'el-radio-group',
        tagIcon: 'radio',
        defaultValue: q.answer || undefined,
        optionType: 'default',
        border: false,
        document: 'https://element.eleme.cn/#/zh-CN/component/radio'
      },
      __slot__: {
        options: (q.options && q.options.length ? q.options : DEFAULT_OPTIONS).map((o) => ({
          label: o.label || o.value || '',
          value: o.value
        }))
      },
      style: {},
      size: 'medium',
      disabled: false,
      __vModel__: q.vModel || `field${formId}`
    }
  }

  if (q.type === 'multi') {
    return {
      __config__: {
        ...baseConfig,
        tag: 'el-checkbox-group',
        tagIcon: 'checkbox',
        defaultValue: Array.isArray(q.answer) ? q.answer : [],
        optionType: 'default',
        border: false,
        document: 'https://element.eleme.cn/#/zh-CN/component/checkbox'
      },
      __slot__: {
        options: (q.options && q.options.length ? q.options : DEFAULT_OPTIONS).map((o) => ({
          label: o.label || o.value || '',
          value: o.value
        }))
      },
      style: {},
      size: 'medium',
      min: null,
      max: null,
      disabled: false,
      __vModel__: q.vModel || `field${formId}`
    }
  }

  // attach
  return {
    __config__: {
      ...baseConfig,
      tag: 'el-upload',
      tagIcon: 'upload',
      defaultValue: null,
      showTip: false,
      buttonText: '点击上传',
      fileSize: 2,
      sizeUnit: 'MB',
      document: 'https://element.eleme.cn/#/zh-CN/component/upload',
      wrok_template: q.templateMd5 || '',
      wrok_template_name: q.templateName || ''
    },
    __slot__: { 'list-type': true },
    action: 'https://jsonplaceholder.typicode.com/posts/',
    disabled: false,
    accept: formatsToAccept(q.formats),
    name: 'file',
    'auto-upload': true,
    'list-type': 'text',
    multiple: false,
    __vModel__: q.vModel || `field${formId}`
  }
}

export const toDrawingList = (explain, questions = []) => {
  const list = [buildExplainItem(explain)]
  questions.forEach((q) => {
    list.push(toDrawingItem(q))
  })
  return list
}

export const fromWorkConfig = (raw) => {
  const settings = createDefaultSettings()
  if (!raw) return settings
  let data = raw
  if (typeof raw === 'string') {
    try {
      data = JSON.parse(raw)
    } catch (e) {
      return settings
    }
  }
  Object.keys(settings).forEach((key) => {
    if (data[key] !== undefined && data[key] !== null) {
      settings[key] = data[key]
    }
  })
  // 兼容字段
  if (data.lessonId !== undefined) settings.lessonId = data.lessonId
  if (data.checkCommit !== undefined && data.checkcommit === undefined) {
    settings.checkcommit = data.checkCommit
  }
  if (typeof settings.needStudy === 'number') {
    settings.needStudy = settings.needStudy === 1
  }
  if (settings.score === true || settings.score === false) {
    settings.score = settings.score ? 'true' : 'false'
  }
  if (settings.checkcommit === true || settings.checkcommit === false) {
    settings.checkcommit = settings.checkcommit ? 'true' : 'false'
  }
  if (settings.isCheck === true || settings.isCheck === false) {
    settings.isCheck = settings.isCheck ? 'true' : 'false'
  }
  if (settings.isSettime === true || settings.isSettime === false) {
    settings.isSettime = settings.isSettime ? 'true' : 'false'
  }
  return settings
}

export const toWorkConfig = (settings) => {
  const s = settings || createDefaultSettings()
  const workconfig = {
    topicType: s.topicType,
    topicname: s.topicname,
    subjectValue: s.subjectValue,
    topicBind: s.topicBind,
    courseValue: s.courseValue,
    coverUrl: s.coverUrl || require('@/assets/cover.png'),
    workname: s.workname,
    userValue: s.userValue,
    beginTime: s.beginTime,
    endTime: s.endTime,
    workType: s.workType,
    isSettime: s.isSettime,
    setTime: s.setTime,
    edit: s.edit,
    isLook: s.isLook,
    supportCopy: s.supportCopy,
    checkCommit: s.checkcommit,
    isCheck: s.isCheck,
    score: s.score,
    fine: s.fine,
    resolve: s.resolve,
    lessonId: s.lessonId,
    subagain: s.subagain
  }
  if (s.subagain) {
    workconfig.subStart = s.subStart
    workconfig.subEnd = s.subEnd
  }
  return workconfig
}

export const formatSupplementTime = (timeStr) => {
  if (!timeStr) return ''
  // yyyy-MM-dd-HH:mm:ss -> yyyy-MM-dd HH:mm:ss
  const arr = timeStr.split('-')
  if (arr.length < 4) return timeStr
  return `${arr[0]}-${arr[1]}-${arr[2]} ${arr[3]}`
}

export const compareTime = (beginTime, endTime) => {
  if (!beginTime || !endTime) return false
  const b = beginTime.split('')
  b.splice(10, 1, ' ')
  const e = endTime.split('')
  e.splice(10, 1, ' ')
  return new Date(e.join('')).getTime() > new Date(b.join('')).getTime()
}
